/**
 * CrewMessagesPage ✦ — /crew/messages
 *
 * Inbox view — supplier ↔ crew threads, newest first. Mobile-
 * first, dark-mode, neon accents. Unread rows carry a subtle
 * pink glow + dot. Tap a row to open the chat.
 *
 * Threads are scoped server-side — supplier sees their crew
 * conversations, crew see their supplier conversations. Same
 * endpoint, role baked into the response by the backend.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChatCircleDots, CaretRight } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.05)';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    const diffMs = now - d;
    const days = Math.floor(diffMs / 86400000);
    if (days < 7) return `${days}d`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { return ''; }
};

const CrewMessagesPage = () => {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/crew/threads`);
      setThreads(r.data?.threads || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/crew/messages');
        return;
      }
      setError(err?.response?.data?.detail || 'Could not load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  return (
    <div data-testid="crew-messages-page" style={{
      minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
      padding: 'clamp(20px, 5vw, 56px) clamp(16px, 5vw, 32px) 80px',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(circle at 18% 14%, ${PURPLE}1F 0%, transparent 42%), radial-gradient(circle at 85% 80%, ${PINK}1A 0%, transparent 42%)`,
      }} />
      <div style={{ maxWidth: 640, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <button
          onClick={() => navigate('/crew/feed')}
          data-testid="messages-back"
          style={{
            background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 24,
          }}
        >
          <ArrowLeft size={14} weight="bold" /> Feed
        </button>

        <p style={{
          margin: '0 0 6px', fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
          textTransform: 'uppercase',
          background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>✦ Inbox</p>
        <h1 style={{ margin: '0 0 6px', fontFamily: FONT_STACK, fontSize: 30, fontWeight: 900, letterSpacing: '-0.02em' }}>
          Messages
        </h1>
        <p style={{ color: TEXT_MUTED, fontSize: 14, marginBottom: 26, lineHeight: 1.55 }}>
          Quick chats with suppliers about your upcoming shifts. Action-first, never noisy.
        </p>

        {loading && <p data-testid="messages-loading" style={{ color: TEXT_MUTED, fontSize: 14 }}>Loading…</p>}
        {error && <p data-testid="messages-error" style={{ color: PINK, fontSize: 14 }}>{error}</p>}

        {!loading && !error && threads.length === 0 && (
          <div data-testid="messages-empty" style={{
            padding: 28, borderRadius: 18, background: PANEL,
            border: `1px dashed ${PANEL_BORDER}`, textAlign: 'center',
          }}>
            <ChatCircleDots size={28} weight="fill" color={PINK} />
            <p style={{ margin: '12px 0 6px', fontWeight: 800, fontSize: 18 }}>No conversations yet</p>
            <p style={{ margin: 0, color: TEXT_MUTED, fontSize: 13.5, lineHeight: 1.55 }}>
              Once a supplier accepts you (or you tap Apply on a card you love), threads will show up here.
            </p>
          </div>
        )}

        {!loading && !error && threads.length > 0 && (
          <ul data-testid="messages-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
            {threads.map((t) => {
              const unread = t.unread > 0;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    data-testid={`thread-row-${t.id}`}
                    data-unread={unread ? '1' : '0'}
                    onClick={() => navigate(`/crew/chat/${t.id}`)}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: 14, borderRadius: 16, cursor: 'pointer',
                      background: unread
                        ? `linear-gradient(135deg, ${PURPLE}22, ${PINK}1A)`
                        : PANEL,
                      border: `1px solid ${unread ? PINK + '66' : PANEL_BORDER}`,
                      boxShadow: unread ? `0 6px 22px ${PINK}33` : 'none',
                      color: 'inherit', fontFamily: FONT_STACK,
                      display: 'flex', alignItems: 'center', gap: 12,
                    }}
                  >
                    <div style={{
                      width: 46, height: 46, borderRadius: '50%',
                      background: t.other_party_picture
                        ? `url(${t.other_party_picture}) center/cover`
                        : `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                      flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 900, fontSize: 15, color: TEXT,
                    }}>
                      {!t.other_party_picture && (t.other_party_name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                        <p style={{
                          margin: 0, fontWeight: 800, fontSize: 14.5,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          flex: 1, minWidth: 0,
                        }}>
                          {t.other_party_name || (t.viewer_role === 'supplier' ? 'Crew member' : 'Supplier')}
                        </p>
                        <span style={{ color: TEXT_MUTED, fontSize: 11, flexShrink: 0 }}>
                          {fmtWhen(t.last_message_at || t.created_at)}
                        </span>
                      </div>
                      <p style={{
                        margin: 0, color: unread ? TEXT : TEXT_MUTED, fontSize: 13,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontWeight: unread ? 700 : 500,
                      }}>
                        {t.last_message_preview || (t.opportunity?.title ? `New thread · ${t.opportunity.title}` : 'New thread')}
                      </p>
                    </div>
                    {unread && (
                      <span data-testid={`thread-unread-${t.id}`} style={{
                        width: 8, height: 8, borderRadius: '50%', background: PINK,
                        boxShadow: `0 0 8px ${PINK}`, flexShrink: 0,
                      }} />
                    )}
                    <CaretRight size={14} weight="bold" color={TEXT_MUTED} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CrewMessagesPage;
